import { Spacing } from '@persona-id/design-tokens';
import { noop } from 'lodash/fp';
import { ChangeEvent, Suspense, useEffect, useMemo, useState, VFC } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, Route, useNavigate } from 'react-router-dom-v5-compat';
import { getFormValues } from 'redux-form';
import styled from 'styled-components';

import { generatePages } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/pages';
import { TOOLBAR_TRANSITION } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditorToolbar';
import DynamicInput from 'dashboard/components/DynamicInput';
import InquiryFlowPreview from 'dashboard/components/InquiryFlowPreview';
import LocalizedDirection from 'dashboard/components/LocalizedDirection';
import NavBack from 'dashboard/components/NavBack';
import ReactSelect from 'dashboard/components/ReactSelect';
import { SentryRoutes } from 'dashboard/components/SentryRoute/SentryRoute';
import ThemeEditorNav from 'dashboard/components/ThemeEditorNav';
import NavConfirmPrompt from 'dashboard/components-lib/NavConfirmPrompt';
import { Header } from 'dashboard/layouts/IndexViewLayoutV2';
import { FULL_SCREEN_HEADER_HEIGHT } from 'dashboard/layouts/ShowViewLayoutV2';
import {
  FORM_THEME_EDITOR_ADVANCED_NAME,
  FORM_THEME_EDITOR_BUTTONS_NAME,
  FORM_THEME_EDITOR_COLOR_NAME,
  FORM_THEME_EDITOR_FONT_NAME,
} from 'dashboard/lib/constants/formNames';
import { mergeResourceWithFormValues } from 'dashboard/lib/utils/formData';
import { InquiryTemplate } from 'dashboard/models/inquiry-template';
import { InquiryTemplateVersion } from 'dashboard/models/inquiry-template-version';
import { Template } from 'dashboard/models/template';
import { Theme, ThemeAttributes } from 'dashboard/models/theme';
import { ThemeSet, ThemeSetAttributes, ThemeSetStyleMap } from 'dashboard/models/theme-set';
import { isThemeEditorFormDirty } from 'dashboard/redux/selectors/theme';
import { RootState } from 'dashboard/redux/store';
import { LoadStylesMapParams } from 'dashboard/redux/thunk-actions/theme-set';
import { getLatestVersionId } from 'dashboard/routes/DashboardThemeSetsEditorRoute/DashboardThemeSetsEditorRoute';
import DashboardEditorAdvancedView from 'dashboard/views/DashboardEditorAdvancedView';
import DashboardEditorButtonsView from 'dashboard/views/DashboardEditorButtonsView';
import DashboardEditorColorView from 'dashboard/views/DashboardEditorColorView';
import DashboardEditorFontView from 'dashboard/views/DashboardEditorFontView';
import { defaultColorAttributes } from 'dashboard/views/DashboardEditorView/DashboardEditorView';
import {
  mergeStyleIntoStepsPreview,
  partitionInUseInquiryTemplates,
} from 'dashboard/views/DashboardThemeSetsEditorView/util';
import DropdownMenu from 'design-system/components/DropdownMenu';
import Spacer from 'design-system/components/Spacer';
import { Label, Paragraph } from 'design-system/components/Text';
import { INQUIRY_LANGUAGES_ENGLISH, InquiryIso6391LanguageCode } from 'lib/constants/locales';
import useToggle from 'lib/hooks/useToggle';
import { ApiModel } from 'lib/interfaces/api';
import type { StepPreview } from 'lib/interfaces/StepsConfig';

const ConfigSection = styled('div')`
  height: calc(100vh - ${FULL_SCREEN_HEADER_HEIGHT}px);
  overflow-y: auto;
  background-color: ${({ theme }) => theme.color.background.primary};
  border-left: 1px solid ${({ theme }) => theme.color.border.primary};
  flex: 0 0 400px;
  transition:
    flex ${TOOLBAR_TRANSITION},
    transform ${TOOLBAR_TRANSITION};
  padding: 0px;
`;

const ConfigContainer = styled('div')`
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const TitleContainer = styled('div')`
  display: flex;
  flex-direction: column;
  border-bottom: 1px solid ${({ theme }) => theme.color.border.primary};
  align-items: flex-start;
`;

const PreviewContainer = styled('div')`
  height: calc(100vh - ${FULL_SCREEN_HEADER_HEIGHT}px);
  width: 100%;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`;

const ContentContainer = styled('div')`
  display: flex;
  flex-direction: row;
`;

const StyledReactSelect = styled(ReactSelect<string>)`
  min-width: 350px;
`;

const HorizontalContainer = styled.div`
  display: flex;
  align-items: center;
`;

const SelectContainer = styled('div')`
  display: flex;
  flex-direction: column;
  padding-left: ${Spacing[4]};
  padding-right: ${Spacing[4]};
  margin-bottom: ${Spacing[1]};
`;

const StyledText = styled('span')`
  font-size: ${({ theme }) => theme.text.paragraph.md.fontSize};
  color: ${({ theme }) => theme.color.text.brand};
  cursor: pointer;
`;

const AdditionalInfoText = styled(Paragraph)`
  font-size: ${({ theme }) => theme.text.paragraph.md.fontSize};
  color: ${({ theme }) => theme.color.text.primary};
`;

interface InUseInquiryTemplatesDropdownProps {
  themeSet: ThemeSet;
  inquiryTemplates: InquiryTemplate[];
}

const InUseInquiryTemplatesDropdown: VFC<InUseInquiryTemplatesDropdownProps> = ({
  themeSet,
  inquiryTemplates,
}) => {
  const [dropdownOpened, { enable: openDropdown, disable: hideDropdown }] = useToggle(false);
  const navigate = useNavigate();
  const associatedInquiryTemplates = useMemo(() => {
    // filter templates that use the currently selected theme as default.
    const [inUseInquiryTemplates] = partitionInUseInquiryTemplates(themeSet, inquiryTemplates, {
      sortByNameDisplay: true,
    });

    // construct count string (singular for count of 1, plural otherwise)
    const templateCount = inUseInquiryTemplates.length;
    const templateNounForm = templateCount <= 1 ? 'template' : 'templates';
    const countLabel = ` ${templateCount} inquiry ${templateNounForm}`;

    const templateLinks = inUseInquiryTemplates.map((template, idx) => {
      const partialUrl = `${template.id}/${template.relationships['latest-draft-version'].data?.id ?? template.relationships['latest-published-version'].data?.id}/flow`;

      return (
        <DropdownMenu.Item
          id={`themeSet-card__template-dropdown-${idx}`}
          key={`themeSet-card__template-dropdown-${idx}`}
          variant='basic'
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/dashboard/inquiry-templates/${partialUrl}`);
          }}
        >
          <StyledText>{template.attributes['name-display']}</StyledText>
        </DropdownMenu.Item>
      );
    });

    return { countLabel, templateLinks };
  }, [themeSet.relationships['inquiry-templates'].data, inquiryTemplates]);

  return (
    <>
      <AdditionalInfoText>
        {associatedInquiryTemplates.templateLinks.length === 0 ? (
          <span>This theme is not being used by any template</span>
        ) : (
          <>
            This theme is used by
            <DropdownMenu
              contentProps={{
                onEscapeKeyDown: hideDropdown,
                onInteractOutside: hideDropdown,
              }}
              open={dropdownOpened}
              onOpenChange={(opened) => opened && openDropdown()}
              trigger={
                <StyledText
                  onMouseEnter={openDropdown}
                  id={`theme-set-card_template-menu-${themeSet.id}`}
                >
                  {associatedInquiryTemplates.countLabel}
                </StyledText>
              }
            >
              {associatedInquiryTemplates.templateLinks}
            </DropdownMenu>
          </>
        )}
      </AdditionalInfoText>
    </>
  );
};

interface DashboardThemeSetsEditorViewProps {
  inquiryTemplates: InquiryTemplate[];
  inquiryTemplateVersion: InquiryTemplateVersion;
  themeSet: ThemeSet;
  stepsPreview: StepPreview[];
  locale: InquiryIso6391LanguageCode;
  styleMap: ThemeSetStyleMap;
  handleUpdateThemeSet: (
    themeSet: ThemeSet,
    themeSetAttributes: Partial<ThemeSetAttributes>,
  ) => Promise<void>;
  handleInquiryTemplateChange: (inquiryTemplateVersionId: string) => Promise<void>;
  handleLocaleChange: (locale: InquiryIso6391LanguageCode) => Promise<void>;
  handleThemeAttributesChange: (loadStyleMapParam: LoadStylesMapParams) => Promise<void>;
}

const DashboardThemeSetsEditorView = ({
  inquiryTemplates,
  inquiryTemplateVersion,
  themeSet,
  stepsPreview,
  locale,
  styleMap,
  handleUpdateThemeSet,
  handleInquiryTemplateChange,
  handleLocaleChange,
  handleThemeAttributesChange,
}: DashboardThemeSetsEditorViewProps) => {
  const [slideNumber, setSlideNumber] = useState(0);
  const [themeSetNameState, setThemeSetNameState] = useState(themeSet.attributes.name);

  const orgDefaultThemeSetId = useSelector<RootState, string | null>(
    (state) => state.organization.organization?.relationships['theme-set'].data?.id,
  );

  const inquiryTemplatesOptions = useMemo(() => {
    const [inUseTemplates, notInUseTemplates] = partitionInUseInquiryTemplates(
      themeSet,
      inquiryTemplates,
      { sortByNameDisplay: true },
    );
    const inUseTemplateOptions = inUseTemplates.map((inqTemp) => ({
      value: inqTemp.id,
      label: inqTemp.attributes['name-display'],
    }));
    const notInUseTemplateOptions = notInUseTemplates.map((inqTemp) => ({
      value: inqTemp.id,
      label: inqTemp.attributes['name-display'],
    }));

    if (inUseTemplateOptions.length > 0) {
      return [
        {
          label: 'Templates using theme as default',
          options: inUseTemplateOptions,
        },
        {
          label: 'Other templates',
          options: notInUseTemplateOptions,
        },
      ];
    } else {
      return notInUseTemplateOptions;
    }
  }, [inquiryTemplates, themeSet]);

  const languageOptions = useMemo(
    () =>
      inquiryTemplateVersion.attributes['enabled-locales'].map((enabledLocale) => ({
        value: enabledLocale,
        label: INQUIRY_LANGUAGES_ENGLISH[enabledLocale],
      })),
    [inquiryTemplateVersion],
  );

  const formValues = useSelector<RootState, any>(
    (state) =>
      getFormValues(FORM_THEME_EDITOR_ADVANCED_NAME)(state) ??
      getFormValues(FORM_THEME_EDITOR_BUTTONS_NAME)(state) ??
      getFormValues(FORM_THEME_EDITOR_COLOR_NAME)(state) ??
      getFormValues(FORM_THEME_EDITOR_FONT_NAME)(state) ??
      {},
  );

  const hasUnsavedChanges = useSelector<RootState, boolean>(isThemeEditorFormDirty);

  const pages = useMemo<React.ReactElement[]>(
    () =>
      Object.values(generatePages(mergeStyleIntoStepsPreview(stepsPreview, styleMap))).flatMap(
        (pages) => pages,
      ),
    [stepsPreview, styleMap],
  );

  const updateThemeSet = async (attributes: ThemeAttributes) => {
    await handleUpdateThemeSet(themeSet, {
      name: themeSetNameState,
      'light-theme': attributes,
    });
  };

  const themeSelectedPristine = useMemo<Theme>(
    () => ({
      id: themeSet.id,
      type: ApiModel.Theme,
      attributes: themeSet.attributes['light-theme'],
    }),
    [themeSet],
  );

  const themeLiveEdited = useMemo<Theme>(
    () => mergeResourceWithFormValues<Theme>(themeSelectedPristine, formValues.theme),
    [themeSelectedPristine, formValues.theme],
  );

  useEffect(() => {
    handleThemeAttributesChange({ themeAttributes: themeLiveEdited.attributes });
  }, [themeLiveEdited.attributes]);

  const settings = {
    arrows: false,
    centerMode: true,
    dots: true,
    focusOnSelect: true,
    infinite: false,
    speed: 0,
    swipeToSlide: false,
    variableWidth: true,
    initialSlide: slideNumber,
    afterChange: (index: number) => {
      setSlideNumber(index);
    },
  };

  const editorPanelProps = {
    handleSave: async () => updateThemeSet(themeLiveEdited.attributes),
    handleTemplateSelect: () => noop(),
    handleThemeSelect: () => noop(),
    loading: false,
    templates: [] as Template[],
    templateSelectedPristine: {} as Template,
    themes: [] as Theme[],
    themeSelectedPristine,
    disabled: false,
    reactRouterV6: true,
  };

  return (
    <>
      <NavConfirmPrompt prevent={hasUnsavedChanges} />
      <Header
        title={
          <HorizontalContainer>
            <NavBack to='/dashboard/theme-sets' />
            <DynamicInput
              aria-label="Edit Theme set's name"
              value={themeSetNameState}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                setThemeSetNameState(e.target.value);
              }}
              onBlur={() => updateThemeSet(themeSet.attributes['light-theme'])}
            />
          </HorizontalContainer>
        }
      />
      <Suspense fallback={null}>
        <LocalizedDirection>
          <ContentContainer>
            <ConfigSection>
              <SentryRoutes>
                <Route
                  index
                  element={
                    <ConfigContainer>
                      <div>
                        <TitleContainer>
                          <Spacer height={3} />
                          <SelectContainer>
                            {orgDefaultThemeSetId === themeSet.id ? (
                              <>
                                <AdditionalInfoText>
                                  This theme is the organization default
                                </AdditionalInfoText>
                              </>
                            ) : (
                              <InUseInquiryTemplatesDropdown
                                themeSet={themeSet}
                                inquiryTemplates={inquiryTemplates}
                              />
                            )}
                          </SelectContainer>
                          <Spacer height={3} />
                          <SelectContainer>
                            <Label
                              size='md'
                              weight='bold'
                              id='edit-inquiry-template-preview-dropdown'
                            >
                              Inquiry Template Previews
                            </Label>
                            <Spacer height={2} />
                            <StyledReactSelect
                              id='edit-inquiry-template-preview-dropdown'
                              aria-labelledby='edit-inquiry-template-preview-dropdown'
                              value={
                                inquiryTemplateVersion.relationships['inquiry-template'].data.id
                              }
                              options={inquiryTemplatesOptions}
                              onChange={(val: string) => {
                                handleInquiryTemplateChange(
                                  getLatestVersionId(
                                    inquiryTemplates.find((it) => it.id === val) as InquiryTemplate,
                                  ) as string,
                                );
                                setSlideNumber(0);
                              }}
                            />
                          </SelectContainer>
                          <Spacer height={3} />
                          <SelectContainer>
                            <Label
                              size='md'
                              weight='bold'
                              id='edit-locale-language-preview-dropdown'
                            >
                              Languages
                            </Label>
                            <Spacer height={2} />
                            <StyledReactSelect
                              id='edit-locale-language-preview-dropdown'
                              aria-labelledby='edit-locale-language-preview-dropdown'
                              value={locale}
                              options={languageOptions}
                              onChange={handleLocaleChange}
                            />
                          </SelectContainer>
                          <Spacer height={3} />
                        </TitleContainer>
                        <ThemeEditorNav
                          showStartPage={false}
                          showSelectCountryPage={false}
                          showDocumentsUploadPage={false}
                          showTerminalPages={false}
                        />
                      </div>
                    </ConfigContainer>
                  }
                />

                <Route
                  path='color'
                  element={
                    <DashboardEditorColorView
                      {...editorPanelProps}
                      defaultColorAttributes={defaultColorAttributes}
                    />
                  }
                />

                <Route path='font' element={<DashboardEditorFontView {...editorPanelProps} />} />

                <Route
                  path='buttons'
                  element={<DashboardEditorButtonsView {...editorPanelProps} />}
                />

                <Route
                  path='advanced'
                  element={
                    <DashboardEditorAdvancedView
                      {...editorPanelProps}
                      defaultColorAttributes={defaultColorAttributes}
                    />
                  }
                />

                <Route path='*' element={<Navigate to='.' replace />} />
              </SentryRoutes>
            </ConfigSection>
            <PreviewContainer>
              <InquiryFlowPreview
                theme={themeLiveEdited}
                templateId={inquiryTemplateVersion?.id}
                pages={pages}
                settings={settings}
                showProgressBar={false}
              />
            </PreviewContainer>
          </ContentContainer>
        </LocalizedDirection>
      </Suspense>
    </>
  );
};

export default DashboardThemeSetsEditorView;
